import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Calculator, EuroIcon, CheckCircle2, ExternalLink } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppContext } from '@/contexts/AppContext';

const formSchema = z.object({
  employees: z.coerce.number().min(1, 'Mindestens 1 Mitarbeiter').default(2),
  minutes: z.coerce.number().min(1, 'Mindestens 1 Minute').default(15),
  hourlyRate: z.coerce.number().min(1, 'Mindestens 1 €').default(43.40),
  workingDays: z.coerce.number().min(1, 'Mindestens 1 Tag').default(230),
  frequency: z.enum(['daily', 'weekly']).default('daily'),
});

type FormValues = z.infer<typeof formSchema>;

const SavingsCalculator = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useAppContext();
  const [savings, setSavings] = useState<number | null>(null);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      employees: 2,
      minutes: 15,
      hourlyRate: 43.40,
      workingDays: 230,
      frequency: 'daily',
    },
  });

  const calculateSavings = (values: FormValues) => {
    const { employees, minutes, hourlyRate, workingDays, frequency } = values;
    // Convert minutes to hours
    const hoursPerDay = (minutes / 60);
    
    // Calculate frequency multiplier
    // If frequency is weekly, divide by 5 (assuming 5 working days per week)
    const frequencyMultiplier = frequency === 'daily' ? 1 : 1/5;
    
    // Calculate total cost per year
    const annualCost = employees * hoursPerDay * hourlyRate * workingDays * frequencyMultiplier;
    return annualCost;
  };

  // Watch for changes in all form fields and recalculate
  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.employees && values.minutes && values.hourlyRate && values.workingDays) {
        const calculatedSavings = calculateSavings(values as FormValues);
        setSavings(calculatedSavings);
      }
    });
    
    // Cleanup subscription on component unmount
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Calculate initial savings on component mount
  useEffect(() => {
    const initialSavings = calculateSavings(form.getValues());
    setSavings(initialSavings);
  }, []);

  return (
    <section className={`py-16 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
      <div className="container">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : ''}`}>
              {t('calculator.title')}
            </h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>
              {t('calculator.description')}
            </p>
          </div>
          
          <Card className={`shadow-lg ${isDarkMode ? 'bg-gray-700 border-gray-600' : ''}`}>
            <CardHeader className={`${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-t-lg`}>
              <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                <Calculator className="h-5 w-5" /> 
                {t('calculator.cardTitle')}
              </CardTitle>
              <CardDescription className={isDarkMode ? 'text-gray-300' : ''}>
                {t('calculator.cardDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <Form {...form}>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="employees"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDarkMode ? 'text-white' : ''}>
                            {t('calculator.employees.label')}
                          </FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="1" 
                              {...field}
                              className={isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : ''} 
                            />
                          </FormControl>
                          <FormDescription className={isDarkMode ? 'text-gray-300' : ''}>
                            {t('calculator.employees.description')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="minutes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDarkMode ? 'text-white' : ''}>
                            {t('calculator.minutes.label')}
                          </FormLabel>
                          <div className="flex gap-2 items-start">
                            <div className="flex-1">
                              <FormControl>
                                <Input 
                                  type="number" 
                                  placeholder="15" 
                                  {...field}
                                  className={isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : ''} 
                                />
                              </FormControl>
                            </div>
                            <FormField
                              control={form.control}
                              name="frequency"
                              render={({ field }) => (
                                <FormControl>
                                  <ToggleGroup 
                                    type="single" 
                                    value={field.value}
                                    onValueChange={(value) => {
                                      if (value) field.onChange(value);
                                    }}
                                    className={`justify-start h-10 ${isDarkMode ? 'bg-gray-600' : ''}`}
                                  >
                                    <ToggleGroupItem 
                                      value="daily" 
                                      aria-label={t('calculator.frequency.daily')}
                                      className={`${isDarkMode 
                                        ? 'data-[state=on]:bg-blue-800 data-[state=on]:text-blue-200' 
                                        : 'data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700'} data-[state=on]:font-medium
                                        ${isDarkMode ? 'text-gray-200 hover:bg-gray-500' : ''}`}
                                    >
                                      {t('calculator.frequency.daily')}
                                    </ToggleGroupItem>
                                    <ToggleGroupItem 
                                      value="weekly" 
                                      aria-label={t('calculator.frequency.weekly')}
                                      className={`${isDarkMode 
                                        ? 'data-[state=on]:bg-blue-800 data-[state=on]:text-blue-200' 
                                        : 'data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700'} data-[state=on]:font-medium
                                        ${isDarkMode ? 'text-gray-200 hover:bg-gray-500' : ''}`}
                                    >
                                      {t('calculator.frequency.weekly')}
                                    </ToggleGroupItem>
                                  </ToggleGroup>
                                </FormControl>
                              )}
                            />
                          </div>
                          <FormDescription className={isDarkMode ? 'text-gray-300' : ''}>
                            {t('calculator.minutes.description')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={form.control}
                      name="hourlyRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDarkMode ? 'text-white' : ''}>
                            {t('calculator.hourlyRate.label')}
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input 
                                type="number" 
                                step="0.01" 
                                placeholder="43.40" 
                                className={`pl-8 ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : ''}`}
                                {...field} 
                              />
                              <EuroIcon className={`absolute left-2.5 top-2.5 h-5 w-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`} />
                            </div>
                          </FormControl>
                          <FormDescription className={isDarkMode ? 'text-gray-300' : ''}>
                            {t('calculator.hourlyRate.description')}
                            <a 
                              href="https://www.destatis.de/DE/Themen/Arbeit/Arbeitskosten-Lohnnebenkosten/_inhalt.html" 
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${isDarkMode ? 'text-blue-300' : 'text-blue-500'} hover:underline ml-1 inline-flex items-center`}
                            >
                              {t('calculator.hourlyRate.source')}
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="workingDays"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isDarkMode ? 'text-white' : ''}>
                            {t('calculator.workingDays.label')}
                          </FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="230" 
                              {...field}
                              className={isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : ''} 
                            />
                          </FormControl>
                          <FormDescription className={isDarkMode ? 'text-gray-300' : ''}>
                            {t('calculator.workingDays.description')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </form>
              </Form>
            </CardContent>
            {savings !== null && (
              <CardFooter className={`${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} flex flex-col items-center py-6 rounded-b-lg space-y-6`}>
                <div className="text-center">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                    {t('calculator.result.label')}
                  </p>
                  <p className={`text-4xl font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} flex items-center justify-center`}>
                    <EuroIcon className="mr-1 h-6 w-6" />
                    {savings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className={`text-sm font-medium mt-2 ${isDarkMode ? 'text-orange-300' : 'text-orange-500'}`}>
                    {t('calculator.result.threeYears', { amount: (savings * 3).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                  </p>
                </div>
                
                <div className="w-full max-w-md">
                  <h4 className={`text-center ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} font-semibold mb-3`}>
                    {t('calculator.benefits.title')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {['satisfaction', 'errors', 'response', 'service', 'focus', 'scalability'].map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className={`h-4 w-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} flex-shrink-0`} />
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {t(`calculator.benefits.${benefit}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button 
                  size="lg" 
                  className={`${isDarkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} mt-4`}
                  asChild
                >
                  <a href="#contact">
                    {t('calculator.cta')}
                  </a>
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator; 